import type {
  Basket,
  Counterparty,
  Currency,
  Customer,
  CustomerConfiguration,
  CustomerQuote,
  ExecutionReport,
  ICustomerExecutionReport,
  MarketAccount,
  MarketAccountApiPermissionFilter,
  MarketAccountsContextProps,
  MarketsContextProps,
  Order,
  PriceProtectionEnum,
  Quote,
  SecuritiesContextProps,
  SecurityTypes,
  StrategiesContextProps,
  UnifiedLiquidityContextProps,
} from '@talos/kyoko';
import type { AppConfigState } from '../../providers/AppConfigProvider/AppConfigProvider.types';
import type { MarketSelectorPreset } from '../MarketSelector/types';

import type { AdjustedSettlementDateResponse } from '../../containers/Blotters/CareOrders/adjustedSettlementDateApi';
import type { IFixingIndexContext } from '../../providers/FixingIndexProvider';
import type { IFundDefinitionContext } from '../../providers/FundDefinitionProvider';
import type { IOrderPreset } from '../../providers/OrderPresetsContext';
import type { CompleteViewLaunchDataMap, OMSView } from './OMSView';
import type { OMS_POPOUT_GROUP_VIEWS } from './viewsWithTabs';

export type MarketAccountReferenceData = Pick<
  MarketAccountsContextProps,
  'marketAccountsByMarket' | 'marketAccountsList' | 'marketAccountsByName'
> & { isReady: boolean };

export type SecuritiesReferenceData = Pick<
  SecuritiesContextProps,
  | 'securitiesList'
  | 'securitiesBySymbol'
  | 'futures'
  | 'futuresByCurrencyPair'
  | 'futuresByMarketByCurrency'
  | 'perps'
  | 'perpsByCurrencyPair'
> & { isReady: boolean };

export type FixingIndicesReferenceData = Pick<
  IFixingIndexContext,
  'fixingIndicesList' | 'fixingIndicesByName' | 'tradableIndicesBySymbol' | 'marketsByFixingIndex'
> & { isReady: boolean };

export type FundDefinitionReferenceData = Pick<IFundDefinitionContext, 'fundsBySymbol' | 'fundsList'>;

export interface AdjustedSettlementDatesReferenceData {
  settlementDates: AdjustedSettlementDateResponse[];
}

export interface BasketReferenceData {
  basketsList: Basket[];
}

export type StrategiesReferenceData = Pick<
  StrategiesContextProps,
  'strategiesList' | 'strategiesByName' | 'ddhStrategiesList'
> & { isReady: boolean };

export interface MarketsReferenceData extends Pick<MarketsContextProps, 'marketsList' | 'marketsByName'> {
  isReady: boolean;
}

export type OptionRefData = {
  options: SecurityTypes['Option'][];
  expirationByMarketByCurrencyIdentity: Map<string, Map<string, Set<string>>>;
  optionSecurityBySymbol: Map<string, SecurityTypes['Option']>;
  strikesByOptionKey: Map<string, string[]>;
  isReady: boolean;
};

export type CurrenciesRefData = {
  currenciesBySymbol: Map<string, Currency>;
  isReady: boolean;
};

export type OrderPresetsRefData = {
  orderPresetsList: IOrderPreset[];
  orderPresetsByID: Map<string, IOrderPreset>;
  isReady: boolean;
};

export interface MarketAccountPermissionFiltersRefData {
  marketAccountFilterIDsBySubAccountName: Map<string, Set<string>>;
  marketAccountPermissionFiltersByFilterID: Map<string, MarketAccountApiPermissionFilter>;
  isReady: boolean;
}

export type UnifiedLiquidityTokenReferenceData = UnifiedLiquidityContextProps['unifiedLiquidityTokenBySymbol'];

// NOTE: we are not deep copying the objects
// Simply keeping reference to them which in turn share the underlying references for ease of use in other slices
export interface OMSReferenceDataState {
  defaultSubAccount?: string;
  customers: Customer[];
  customerMarketAccounts: MarketAccount[];
  customerConfigurations: CustomerConfiguration[];

  securities: SecuritiesReferenceData;
  strategies: StrategiesReferenceData;
  markets: MarketsReferenceData;
  marketAccounts: MarketAccountReferenceData;
  currencies: CurrenciesRefData;
  options: OptionRefData;
  orderPresets: OrderPresetsRefData;
  counterparties: Counterparty[];
  marketAccountPermissionFilters: MarketAccountPermissionFiltersRefData;

  settings: OMSSettingsState;
  unifiedLiquidityTokens: UnifiedLiquidityTokenReferenceData;
  fixingIndices: FixingIndicesReferenceData;
  baskets: BasketReferenceData;
  funds: FundDefinitionReferenceData;
  adjustedSettlementDates: AdjustedSettlementDatesReferenceData;

  tradingAggregationPresets: MarketSelectorPreset[] | undefined;
}

export type BasketSettleOffsetOverride = Record<string, { Sell?: number; Buy?: number }> | undefined;

type OMSFeatureFlags = {
  defaultPriceProtection: PriceProtectionEnum;
  supportedStrategiesForPricingModes: string[];
  enableOptionTrading: boolean;
  enableNegotiatedRFQFlow: boolean;
  enableTakerMarketplaceETFRFQFlow: boolean;
  truncateCareOrderID: boolean;
  enableAccountSegregation: boolean;
  enableMSSValidation: boolean;
  marketSelectionAsyncRefDataTimeoutMs: number;
  disableNonSTFNegotiatedRFQ: boolean;
  disableUnifiedLiquidityDerivativesMarketSelectionRestriction: boolean;
  disableCrossCurrencyQty: boolean;
  nonSpotDelta1DefaultQty: string;
  enableRFQFormFundSecurities: boolean;
  enableRFQFormOptions: boolean;
  enableRFQSizeCounteringMultitokenCompound: boolean;
  enableRFQSizeCounteringSpot: boolean;
  fundCareOrderDefaultSubAccounts: Record<string, string> | undefined;
  useFundDefinitionAndTradableIndexesStreams: boolean;
  supportAdjustedSettlementDate: boolean;
  defaultBasketSettleOffsetOverride: BasketSettleOffsetOverride;
  enableRFQFormFundField: boolean;
  enableDurationSubmission: boolean;
};

export const MARKET_SELECTION_ASYNC_REFDATA_TIMEOUT_MS = 1500;

export interface OMSSettingsState
  extends
    OMSFeatureFlags,
    Pick<
      AppConfigState,
      | 'lastStrategyUsed'
      | 'useTradeAllocations'
      | 'enableGroups'
      | 'enableDerivativeContractDefault'
      | 'alwaysCheckPriceReasonability'
      | 'defaultOrderStrategy'
      | 'defaultMultilegOrderStrategy'
      | 'showAllInPrices'
      | 'showDelta1BPS'
      | 'allowSyntheticCcy'
      | 'allowSyntheticCrosses'
      | 'exclusivelyPrimePriceOnRePriming'
      | 'enableReduceFirst'
      | 'enableReduceOnly'
      | 'ddhDefaultsByID'
      | 'defaultToEmptyOrderMarkets'
      | 'defaultMarketAccounts'
      | 'defaultAggregationId'
      | 'resetOrderAccountsOnSubAccountChange'
      | 'rfqFormTradingProtocol'
    > {}

export type OMSPopoutGroupType = keyof typeof OMS_POPOUT_GROUP_VIEWS;

export interface OMSPopoutGroup {
  name: OMSPopoutGroupType;
  views: OMSView[];
  activeView: OMSView;
  features: OMSPopoutFeatures;
  /** We need to keep a reference to the window object to move focus to popouts */
  _window?: Window;
}

export interface OMSPopoutFeatures {
  width: number;
  height: number;
  left: number;
  top: number;
  [name: string]: number | string | boolean | undefined;
}

export type Entity = Order | Quote | CustomerQuote | ICustomerExecutionReport | ExecutionReport;

export interface OMSState {
  openedView?: OMSView;
  popoutGroups: Partial<Record<OMSPopoutGroupType, OMSPopoutGroup>>;
  placedEntity?: Entity | null;
  openedViewLaunchData?: CompleteViewLaunchDataMap[OMSView] | undefined;
}

export type OMSContentProps = {
  view?: OMSView;
  onClose: () => void;
  onChangeTab?: (view: OMSView) => void;
} & (
  | {
      onPopout: (popoutGroupName: OMSPopoutGroupType) => void;
      isPopout?: false;
    }
  | {
      isPopout: true;
      onPopout?: undefined;
    }
);

export interface OMSFormProps {
  isPopout: boolean;
}
